import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { 
  Ticket, Calendar, Wrench, ArrowRight, CheckCircle2, 
  Clock, User, MapPin, Zap, ChevronRight, Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * QuickWorkflow Component
 * Permette la conversione rapida: Ticket → Appuntamento → Intervento
 */
export function QuickWorkflow({ 
  ticket, 
  open, 
  onOpenChange, 
  onComplete,
  tecnici = [],
  puntiVendita = []
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Review, 2: Appuntamento, 3: Confirm
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Appuntamento
    data_ora: '',
    durata_minuti: 60,
    tecnico_id: ticket?.tecnico_assegnato_id || '',
    note_appuntamento: '',
    // Intervento
    descrizione: ticket?.descrizione || '',
    note_intervento: '',
    crea_intervento: false // Se true, crea anche intervento dopo appuntamento
  });
  const [createdAppuntamento, setCreatedAppuntamento] = useState(null);

  const resetWorkflow = () => {
    setStep(1);
    setFormData({
      data_ora: '',
      durata_minuti: 60,
      tecnico_id: ticket?.tecnico_assegnato_id || '',
      note_appuntamento: '',
      descrizione: ticket?.descrizione || '',
      note_intervento: '',
      crea_intervento: false
    });
    setCreatedAppuntamento(null);
  };

  const handleCreateAppuntamento = async () => {
    if (!formData.data_ora || !formData.tecnico_id) {
      toast.error('Compila data/ora e tecnico');
      return;
    }

    setLoading(true);
    try {
      const appuntamentoData = {
        titolo: `[Ticket #${ticket.numero}] ${ticket.oggetto}`,
        cliente_id: ticket.cliente_id,
        tecnico_id: formData.tecnico_id,
        data_ora: formData.data_ora,
        durata_minuti: formData.durata_minuti,
        tipo: 'intervento',
        note: formData.note_appuntamento || `Riferimento Ticket #${ticket.numero}`,
        ticket_id: ticket.id
      };

      const response = await axios.post(`${API}/appuntamenti`, appuntamentoData);
      setCreatedAppuntamento(response.data);
      
      // Aggiorna ticket a "in_lavorazione"
      await axios.patch(`${API}/tickets/${ticket.id}`, { stato: 'in_lavorazione' });
      
      toast.success('Appuntamento creato!');
      
      if (formData.crea_intervento) {
        setStep(3);
      } else {
        onComplete?.();
        onOpenChange(false);
        resetWorkflow();
      }
    } catch (error) {
      console.error('Error creating appuntamento:', error);
      toast.error('Errore nella creazione dell\'appuntamento');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIntervento = async () => {
    setLoading(true);
    try {
      const interventoData = {
        cliente_id: ticket.cliente_id,
        ticket_id: ticket.id,
        appuntamento_id: createdAppuntamento?.id,
        tecnico_id: formData.tecnico_id,
        data_intervento: formData.data_ora?.split('T')[0],
        descrizione: formData.descrizione,
        note: formData.note_intervento,
        stato: 'completato',
        tipo_intervento: 'assistenza'
      };

      await axios.post(`${API}/interventi`, interventoData);
      
      // Chiudi ticket
      await axios.patch(`${API}/tickets/${ticket.id}`, { stato: 'chiuso' });
      
      toast.success('Intervento creato e ticket chiuso!');
      onComplete?.();
      onOpenChange(false);
      resetWorkflow();
    } catch (error) {
      console.error('Error creating intervento:', error);
      toast.error('Errore nella creazione dell\'intervento');
    } finally {
      setLoading(false);
    }
  };

  if (!ticket) return null;

  const tecnico = tecnici.find(t => t.id === formData.tecnico_id);

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) resetWorkflow(); }}>
      <DialogContent className="sm:max-w-xl rounded-md">
        <DialogHeader>
          <DialogTitle className="font-mono uppercase tracking-wider flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Workflow 1-Click
          </DialogTitle>
          <DialogDescription>
            Converti rapidamente un ticket in appuntamento e intervento
          </DialogDescription>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 py-4">
          {[
            { num: 1, label: 'Ticket', icon: Ticket },
            { num: 2, label: 'Appuntamento', icon: Calendar },
            { num: 3, label: 'Intervento', icon: Wrench }
          ].map(({ num, label, icon: Icon }, index) => (
            <div key={num} className="flex items-center">
              <div className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-md transition-all",
                step >= num ? "bg-cyan-500/20 text-cyan-500" : "bg-muted text-muted-foreground"
              )}>
                <Icon className="h-4 w-4" />
                <span className="text-xs font-medium hidden sm:inline">{label}</span>
                {step > num && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
              </div>
              {index < 2 && <ChevronRight className="h-4 w-4 text-muted-foreground mx-1" />}
            </div>
          ))}
        </div>

        {/* Step 1: Review Ticket */}
        {step === 1 && (
          <div className="space-y-4">
            <Card className="rounded-md border-blue-500/30 bg-blue-500/5">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge variant="outline" className="rounded text-xs font-mono mb-2">
                      Ticket #{ticket.numero}
                    </Badge>
                    <h3 className="font-bold">{ticket.oggetto}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{ticket.descrizione}</p>
                  </div>
                  <Badge className={cn(
                    "rounded text-xs",
                    ticket.priorita === 'urgente' ? 'bg-red-500' :
                    ticket.priorita === 'alta' ? 'bg-amber-500' :
                    ticket.priorita === 'media' ? 'bg-blue-500' : 'bg-gray-500'
                  )}>
                    {ticket.priorita}
                  </Badge>
                </div>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {ticket.cliente_nome || 'Cliente'}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {new Date(ticket.created_at).toLocaleDateString('it-IT')}
                  </span>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md">
                Annulla
              </Button>
              <Button onClick={() => setStep(2)} className="rounded-md bg-cyan-500 hover:bg-cyan-600">
                <Calendar className="h-4 w-4 mr-2" />
                Crea Appuntamento
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Create Appuntamento */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase">Data e Ora *</Label>
                <Input
                  type="datetime-local"
                  value={formData.data_ora}
                  onChange={(e) => setFormData({...formData, data_ora: e.target.value})}
                  className="rounded-md"
                  data-testid="workflow-datetime"
                />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase">Durata (min)</Label>
                <Select 
                  value={formData.durata_minuti.toString()} 
                  onValueChange={(v) => setFormData({...formData, durata_minuti: parseInt(v)})}
                >
                  <SelectTrigger className="rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-md">
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="60">1 ora</SelectItem>
                    <SelectItem value="90">1.5 ore</SelectItem>
                    <SelectItem value="120">2 ore</SelectItem>
                    <SelectItem value="180">3 ore</SelectItem>
                    <SelectItem value="240">4 ore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase">Tecnico *</Label>
              <Select 
                value={formData.tecnico_id} 
                onValueChange={(v) => setFormData({...formData, tecnico_id: v})}
              >
                <SelectTrigger className="rounded-md" data-testid="workflow-tecnico">
                  <SelectValue placeholder="Seleziona tecnico" />
                </SelectTrigger>
                <SelectContent className="rounded-md">
                  {tecnici.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.nome} {t.cognome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-mono uppercase">Note Appuntamento</Label>
              <Textarea
                value={formData.note_appuntamento}
                onChange={(e) => setFormData({...formData, note_appuntamento: e.target.value})}
                className="rounded-md"
                placeholder="Note aggiuntive per l'appuntamento..."
                rows={2}
              />
            </div>

            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md">
              <input
                type="checkbox"
                id="crea_intervento"
                checked={formData.crea_intervento}
                onChange={(e) => setFormData({...formData, crea_intervento: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="crea_intervento" className="text-sm cursor-pointer">
                <span className="font-medium">Crea anche intervento</span>
                <span className="text-muted-foreground ml-1">(chiude automaticamente il ticket)</span>
              </label>
            </div>

            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="rounded-md">
                Indietro
              </Button>
              <Button 
                onClick={handleCreateAppuntamento} 
                disabled={loading || !formData.data_ora || !formData.tecnico_id}
                className="rounded-md bg-cyan-500 hover:bg-cyan-600"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                {formData.crea_intervento ? 'Continua' : 'Crea Appuntamento'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Create Intervento (optional) */}
        {step === 3 && (
          <div className="space-y-4">
            <Card className="rounded-md border-emerald-500/30 bg-emerald-500/5">
              <CardContent className="p-4 flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <div>
                  <p className="font-medium">Appuntamento creato!</p>
                  <p className="text-sm text-muted-foreground">
                    {tecnico && `${tecnico.nome} ${tecnico.cognome}`} - {formData.data_ora && new Date(formData.data_ora).toLocaleString('it-IT')}
                  </p>
                </div>
              </CardContent>
            </Card>

            <div>
              <Label className="text-xs font-mono uppercase">Descrizione Intervento</Label>
              <Textarea
                value={formData.descrizione}
                onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                className="rounded-md"
                rows={3}
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase">Note Intervento</Label>
              <Textarea
                value={formData.note_intervento}
                onChange={(e) => setFormData({...formData, note_intervento: e.target.value})}
                className="rounded-md"
                placeholder="Lavori eseguiti, note tecniche..."
                rows={2}
              />
            </div>

            <div className="flex justify-between gap-2">
              <Button 
                variant="outline" 
                onClick={() => { onComplete?.(); onOpenChange(false); resetWorkflow(); }}
                className="rounded-md"
              >
                Solo Appuntamento
              </Button>
              <Button 
                onClick={handleCreateIntervento} 
                disabled={loading}
                className="rounded-md bg-emerald-500 hover:bg-emerald-600"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Wrench className="h-4 w-4 mr-2" />
                )}
                Crea Intervento e Chiudi Ticket
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Quick Convert Button - Da usare nella lista tickets o dettaglio ticket
 */
export function QuickConvertButton({ ticket, tecnici, size = 'default', onComplete }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button 
        variant="outline" 
        size={size}
        onClick={() => setOpen(true)}
        className="rounded-md border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
        data-testid="quick-convert-btn"
      >
        <Zap className="h-4 w-4 mr-1" />
        {size !== 'sm' && 'Quick Convert'}
      </Button>
      
      <QuickWorkflow
        ticket={ticket}
        open={open}
        onOpenChange={setOpen}
        tecnici={tecnici}
        onComplete={onComplete}
      />
    </>
  );
}

export default QuickWorkflow;
